import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { setBingConsent } from '@analytics'
import { selectUserLocation } from '@store/signup'
import { isBingTraffic, isConsentRequiredCountry } from '@utils'

const AD_STORAGE_CATEGORY = 'advertisement'
const CONSENT_UPDATE_EVENT = 'cookieyes_consent_update'
const BANNER_LOADED_EVENT = 'cookieyes_banner_loaded'
const VISIBILITY_ATTRIBUTE = 'data-cookie-banner'
const BANNER_DELAY_MS = 2000

const skipsCookieBanner = () =>
  new URLSearchParams(window.location.search).get('skip_cookies') === 'true'

export const useCookieBanner = () => {
  const userLocation = useSelector(selectUserLocation)

  useEffect(() => {
    if (!isBingTraffic() || !userLocation) return

    const needsConsent = isConsentRequiredCountry(userLocation)
    const showBanner = needsConsent && !skipsCookieBanner()
    document.documentElement.setAttribute(VISIBILITY_ATTRIBUTE, 'hide')

    const showTimer = showBanner
      ? window.setTimeout(() => {
          document.documentElement.setAttribute(VISIBILITY_ATTRIBUTE, 'show')
        }, BANNER_DELAY_MS)
      : undefined

    const applyConsent = () => {
      // Outside the EEA / UK / CH there is no banner: ad storage is granted as
      // soon as the country is known, whatever CookieYes has to say about it.
      if (!needsConsent) {
        setBingConsent('granted')
        return
      }

      const consent = window.getCkyConsent?.()
      if (!consent?.isUserActionCompleted) return
      setBingConsent(consent.categories?.[AD_STORAGE_CATEGORY] ? 'granted' : 'denied')
    }

    // GTM can have loaded the CMP before this ran, so the stored answer is read
    // right away rather than only on the event a returning visitor never fires.
    applyConsent()

    document.addEventListener(CONSENT_UPDATE_EVENT, applyConsent)
    document.addEventListener(BANNER_LOADED_EVENT, applyConsent)

    return () => {
      if (showTimer !== undefined) window.clearTimeout(showTimer)
      document.removeEventListener(CONSENT_UPDATE_EVENT, applyConsent)
      document.removeEventListener(BANNER_LOADED_EVENT, applyConsent)
    }
  }, [userLocation])
}
